import moment from "moment-timezone";
import { z } from "zod";
import { isNil } from "./null.mjs";
import { simplifyZodError } from "./zod.mjs";
const TIME_REGEX = /^(\d{1,2}):(\d{2})$/;
const parseTimeString = (timeString) => {
  const match = TIME_REGEX.exec(timeString.trim());
  const hourString = match?.[1];
  const minuteString = match?.[2];
  if (isNil(hourString) || isNil(minuteString)) {
    throw new Error(`Failed to parse time string: ${timeString}`);
  }
  const hour = Number.parseInt(hourString, 10);
  const minute = Number.parseInt(minuteString, 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    throw new Error(`Failed to parse time string: ${timeString}`);
  }
  return { hour, minute, totalMinutes: hour * 60 + minute };
};
const createWorkingHours = (startTime, endTime, workingDays) => {
  const startParsed = parseTimeString(startTime);
  const endParsed = parseTimeString(endTime);
  for (const day of workingDays) {
    if (!Number.isInteger(day) || day < 1 || day > 7) {
      throw new Error(`Invalid working day: ${day}. Must be an integer between 1 (Monday) and 7 (Sunday)`);
    }
  }
  if (startParsed.totalMinutes >= endParsed.totalMinutes) {
    throw new Error(`Start time must be before end time: ${startTime} >= ${endTime}`);
  }
  const segment = { startTime, endTime };
  const schedule = [];
  for (let day = 1; day <= 7; day++) {
    schedule.push({
      dayOfWeek: day,
      segments: workingDays.includes(day) ? [segment] : []
    });
  }
  return { schedule };
};
const workingHoursSegmentSchema = z.object({
  startTime: z.string().regex(TIME_REGEX, "Must be in HH:mm format"),
  endTime: z.string().regex(TIME_REGEX, "Must be in HH:mm format")
}).refine(
  (segment) => {
    const start = parseTimeString(segment.startTime);
    const end = parseTimeString(segment.endTime);
    return start.totalMinutes < end.totalMinutes;
  },
  { message: "startTime must be before endTime" }
);
const workingHoursDaySchema = z.object({
  dayOfWeek: z.number().int().min(1).max(7),
  segments: z.array(workingHoursSegmentSchema)
}).refine(
  (day) => day.segments.length > 0,
  (day) => ({
    message: `Day ${day.dayOfWeek} contains no time segments. To define availability for this day, include at least one segment. To mark it as non-working, omit the day from the schedule.`
  })
);
const workingHoursSchema = z.object({
  schedule: z.array(workingHoursDaySchema),
  isDefault: z.boolean().optional()
});
function validateWorkingHours(value) {
  const result = workingHoursSchema.safeParse(value);
  if (!result.success) {
    throw simplifyZodError(result.error, { value, schema: workingHoursSchema });
  }
}
function isWithinWorkingHours(date, workingHours, timezone) {
  const momentDate = date instanceof Date ? moment.tz(date, timezone) : moment.tz(date, moment.ISO_8601, true, timezone);
  if (!momentDate.isValid()) return false;
  const daySchedule = workingHours.schedule.find((day) => day.dayOfWeek === momentDate.isoWeekday());
  if (!daySchedule || daySchedule.segments.length === 0) return false;
  const minutes = momentDate.hours() * 60 + momentDate.minutes();
  return daySchedule.segments.some((segment) => {
    const segmentStart = parseTimeString(segment.startTime);
    const segmentEnd = parseTimeString(segment.endTime);
    return minutes >= segmentStart.totalMinutes && minutes < segmentEnd.totalMinutes;
  });
}
function isRangeWithinWorkingHours(startDate, endDate, workingHours, timezone) {
  const start = startDate instanceof Date ? moment.tz(startDate, timezone) : moment.tz(startDate, moment.ISO_8601, true, timezone);
  const end = endDate instanceof Date ? moment.tz(endDate, timezone) : moment.tz(endDate, moment.ISO_8601, true, timezone);
  if (!start.isValid() || !end.isValid()) return false;
  if (start.isSameOrAfter(end)) return false;
  if (!start.isSame(end, "day")) return false;
  const daySchedule = workingHours.schedule.find((day) => day.dayOfWeek === start.isoWeekday());
  if (!daySchedule || daySchedule.segments.length === 0) return false;
  const startMinutes = start.hours() * 60 + start.minutes();
  const endMinutes = end.hours() * 60 + end.minutes();
  return daySchedule.segments.some((segment) => {
    const segmentStart = parseTimeString(segment.startTime).totalMinutes;
    const segmentEnd = parseTimeString(segment.endTime).totalMinutes;
    return startMinutes >= segmentStart && endMinutes <= segmentEnd;
  });
}
export {
  createWorkingHours,
  isRangeWithinWorkingHours,
  isWithinWorkingHours,
  parseTimeString,
  validateWorkingHours
};
//# sourceMappingURL=working-hours.mjs.map
